import { Bell, Building2, ClipboardCheck, FileText, Plus, Target, UserPlus, Users } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react'
import { useNavigate } from 'react-router-dom'

type QuickActionMenuProps = {
  onAddNewProspect: (trigger: HTMLButtonElement) => void
  onAddSalesperson?: (trigger: HTMLButtonElement) => void
  onSetReminder: (trigger: HTMLButtonElement) => void
  onReviewMissingInformation: (trigger: HTMLButtonElement) => void
  onOpenScript?: (trigger: HTMLButtonElement) => void
  variant: 'desktop' | 'mobile'
}

type PresenceState = 'hidden' | 'visible' | 'closing'

type DashboardQuickActionContext = {
  label: string
  trigger: HTMLButtonElement | null
}

const BACKDROP_EXIT_MS = 200
const MENU_EXIT_MS = 260

const SCROLL_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'End',
  'Home',
  'PageDown',
  'PageUp',
  ' ',
])

type DesktopOriginStyle = CSSProperties & {
  '--desktop-origin-x': string
  '--desktop-origin-y': string
}

export function QuickActionMenu({
  onAddNewProspect,
  onAddSalesperson,
  onSetReminder,
  onReviewMissingInformation,
  onOpenScript,
  variant,
}: QuickActionMenuProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const [dashboardContext, setDashboardContext] =
    useState<DashboardQuickActionContext | null>(null)

  const dashboardContextRef =
    useRef<DashboardQuickActionContext | null>(null)

  const [backdropState, setBackdropState] =
    useState<PresenceState>('hidden')

  const [menuState, setMenuState] =
    useState<PresenceState>('hidden')

  const [desktopOrigin, setDesktopOrigin] = useState({
    x: '0px',
    y: '0px',
  })

  const menuId = useId()

  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const addActionRef = useRef<HTMLButtonElement>(null)
  const salespersonActionRef = useRef<HTMLButtonElement>(null)
  const reminderActionRef = useRef<HTMLButtonElement>(null)
  const reviewActionRef = useRef<HTMLButtonElement>(null)

  const scriptActionRef = useRef<HTMLButtonElement>(null)
  const prospectingActionRef = useRef<HTMLButtonElement>(null)
  const salesTeamActionRef = useRef<HTMLButtonElement>(null)
  const backdropTimerRef = useRef<number | null>(null)
  const menuTimerRef = useRef<number | null>(null)
  const actionTimerRef = useRef<number | null>(null)

  const beginExit = useCallback(() => {
    if (backdropTimerRef.current !== null) {
      window.clearTimeout(backdropTimerRef.current)
    }

    if (menuTimerRef.current !== null) {
      window.clearTimeout(menuTimerRef.current)
    }

    const reduceMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ??
      false

    setBackdropState('closing')
    setMenuState('closing')

    backdropTimerRef.current = window.setTimeout(() => {
      backdropTimerRef.current = null
      setBackdropState('hidden')
    }, reduceMotion ? 0 : BACKDROP_EXIT_MS)

    menuTimerRef.current = window.setTimeout(() => {
      menuTimerRef.current = null
      setMenuState('hidden')
    }, reduceMotion ? 0 : MENU_EXIT_MS)
  }, [])

  const beginOpen = useCallback(() => {
    if (backdropTimerRef.current !== null) {
      window.clearTimeout(backdropTimerRef.current)
    }

    if (menuTimerRef.current !== null) {
      window.clearTimeout(menuTimerRef.current)
    }

    backdropTimerRef.current = null
    menuTimerRef.current = null

    if (variant === 'desktop' && triggerRef.current) {
      const triggerBounds =
        triggerRef.current.getBoundingClientRect()

      setDesktopOrigin({
        x: `${
          triggerBounds.left +
          triggerBounds.width / 2 -
          window.innerWidth / 2
        }px`,
        y: `${
          triggerBounds.top +
          triggerBounds.height / 2 -
          window.innerHeight / 2
        }px`,
      })
    }

    setBackdropState('visible')
    setMenuState('visible')
  }, [variant])

  const closeMenu = useCallback(
    (returnFocus: boolean) => {
      beginExit()
      setOpen(false)
      setDashboardContext(null)

      if (returnFocus) {
        window.requestAnimationFrame(() =>
          triggerRef.current?.focus(),
        )
      }
    },
    [beginExit],
  )

  useEffect(() => {
    if (!open) return

    if (dashboardContext) {
      reviewActionRef.current?.focus()
    } else {
      addActionRef.current?.focus()
    }

    function handlePointerDown(event: PointerEvent) {
      if (
        !rootRef.current?.contains(event.target as Node)
      ) {
        closeMenu(false)
      }
    }

    function handleEscape(
      event: globalThis.KeyboardEvent,
    ) {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeMenu(true)
      }
    }

    document.addEventListener(
      'pointerdown',
      handlePointerDown,
    )

    document.addEventListener(
      'keydown',
      handleEscape,
    )

    return () => {
      document.removeEventListener(
        'pointerdown',
        handlePointerDown,
      )

      document.removeEventListener(
        'keydown',
        handleEscape,
      )
    }
  }, [closeMenu, dashboardContext, open])

  useEffect(() => {
    function handleDashboardQuickActions(
      event: Event,
    ) {
      const customEvent = event as CustomEvent<{
        label?: string
        trigger?: HTMLButtonElement
      }>

      const label = customEvent.detail?.label

      if (!label) return

      /*
       * Desktop Sidebar and MobileNav both contain a
       * QuickActionMenu. Only the visible instance should open.
       */
      if (
        !rootRef.current ||
        rootRef.current.getClientRects().length === 0
      ) {
        return
      }

      const nextContext = {
        label,
        trigger:
          customEvent.detail?.trigger ?? null,
      }

      dashboardContextRef.current = nextContext
      setDashboardContext(nextContext)

      beginOpen()
      setOpen(true)
    }

    window.addEventListener(
      'wolf-forge-quick-actions',
      handleDashboardQuickActions,
    )

    return () => {
      window.removeEventListener(
        'wolf-forge-quick-actions',
        handleDashboardQuickActions,
      )
    }
  }, [beginOpen])

  useEffect(
    () => () => {
      if (backdropTimerRef.current !== null) {
        window.clearTimeout(
          backdropTimerRef.current,
        )
      }

      if (menuTimerRef.current !== null) {
        window.clearTimeout(
          menuTimerRef.current,
        )
      }

      if (actionTimerRef.current !== null) {
        window.clearTimeout(
          actionTimerRef.current,
        )
      }
    },
    [],
  )

  useEffect(() => {
    if (
      backdropState === 'hidden' &&
      menuState === 'hidden'
    ) {
      return
    }

    const scrollContainer =
      document.scrollingElement

    const lockedScrollTop =
      scrollContainer?.scrollTop ??
      window.scrollY

    const lockedScrollLeft =
      scrollContainer?.scrollLeft ??
      window.scrollX

    function preventScroll(event: Event) {
      event.preventDefault()
    }

    function preventScrollKeys(
      event: globalThis.KeyboardEvent,
    ) {
      if (SCROLL_KEYS.has(event.key)) {
        event.preventDefault()
      }
    }

    function preserveScrollPosition() {
      if (!scrollContainer) return

      if (
        scrollContainer.scrollTop !==
        lockedScrollTop
      ) {
        scrollContainer.scrollTop =
          lockedScrollTop
      }

      if (
        scrollContainer.scrollLeft !==
        lockedScrollLeft
      ) {
        scrollContainer.scrollLeft =
          lockedScrollLeft
      }
    }

    document.addEventListener(
      'wheel',
      preventScroll,
      { passive: false },
    )

    document.addEventListener(
      'touchmove',
      preventScroll,
      { passive: false },
    )

    document.addEventListener(
      'keydown',
      preventScrollKeys,
    )

    window.addEventListener(
      'scroll',
      preserveScrollPosition,
      { passive: true },
    )

    return () => {
      document.removeEventListener(
        'wheel',
        preventScroll,
      )

      document.removeEventListener(
        'touchmove',
        preventScroll,
      )

      document.removeEventListener(
        'keydown',
        preventScrollKeys,
      )

      window.removeEventListener(
        'scroll',
        preserveScrollPosition,
      )
    }
  }, [backdropState, menuState, variant])

  function handleMenuKeyDown(
    event: KeyboardEvent<HTMLDivElement>,
  ) {
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Home' ||
      event.key === 'End'
    ) {
      event.preventDefault()

      const actions = [
        addActionRef.current,
        salespersonActionRef.current,
        reminderActionRef.current,
        reviewActionRef.current,
        scriptActionRef.current,
        prospectingActionRef.current,
        salesTeamActionRef.current,
      ].filter(
        (
          action,
        ): action is HTMLButtonElement =>
          Boolean(action),
      )

      if (actions.length === 0) return

      const currentIndex = actions.indexOf(
        document.activeElement as HTMLButtonElement,
      )

      if (event.key === 'Home') {
        actions[0].focus()
      } else if (event.key === 'End') {
        actions[actions.length - 1].focus()
      } else if (event.key === 'ArrowDown') {
        actions[
          (currentIndex + 1 + actions.length) %
            actions.length
        ].focus()
      } else {
        actions[
          (currentIndex - 1 + actions.length) %
            actions.length
        ].focus()
      }
    }
  }

  function toggleMenu() {
    if (open) {
      closeMenu(true)
      return
    }

    /*
     * Opening from the normal + button means there is
     * no Dashboard filter context.
     */
    dashboardContextRef.current = null
    setDashboardContext(null)
    beginOpen()
    setOpen(true)
  }

  function selectReview() {
    const trigger = triggerRef.current

    if (!trigger) return

    const context = dashboardContextRef.current

    beginExit()
    setOpen(false)

    const reduceMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false

    actionTimerRef.current =
      window.setTimeout(() => {
        actionTimerRef.current = null

        if (context) {
          window.dispatchEvent(
            new CustomEvent(
              'wolf-forge-review-confirmed',
              {
                detail: {
                  trigger:
                    context.trigger ?? trigger,
                  label: context.label,
                },
              },
            ),
          )

          dashboardContextRef.current = null
          setDashboardContext(null)
          return
        }

        /*
         * Normal + menu behavior remains unchanged.
         */
        onReviewMissingInformation(trigger)
      }, reduceMotion ? 0 : MENU_EXIT_MS)
  }


  function selectReminder() {
    const trigger = triggerRef.current

    if (!trigger) return

    beginExit()
    setOpen(false)
    dashboardContextRef.current = null
    setDashboardContext(null)

    const reduceMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false

    actionTimerRef.current =
      window.setTimeout(() => {
        actionTimerRef.current = null
        onSetReminder(trigger)
      }, reduceMotion ? 0 : MENU_EXIT_MS)
  }
  function selectAddProspect() {
    const trigger = triggerRef.current

    if (!trigger) return

    beginExit()
    setOpen(false)
    dashboardContextRef.current = null
    setDashboardContext(null)

    const reduceMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false

    actionTimerRef.current =
      window.setTimeout(() => {
        actionTimerRef.current = null
        onAddNewProspect(trigger)
      }, reduceMotion ? 0 : MENU_EXIT_MS)
  }

  function selectAddSalesperson() {
    const trigger = triggerRef.current

    if (!trigger) return

    beginExit()
    setOpen(false)
    dashboardContextRef.current = null
    setDashboardContext(null)

    const reduceMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false

    actionTimerRef.current =
      window.setTimeout(() => {
        actionTimerRef.current = null
        onAddSalesperson?.(trigger)
      }, reduceMotion ? 0 : MENU_EXIT_MS)
  }

  function selectScript() {
    const trigger = triggerRef.current

    if (!trigger) return

    beginExit()
    setOpen(false)
    dashboardContextRef.current = null
    setDashboardContext(null)

    const reduceMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false

    actionTimerRef.current =
      window.setTimeout(() => {
        actionTimerRef.current = null
        onOpenScript?.(trigger)
      }, reduceMotion ? 0 : MENU_EXIT_MS)
  }
  function selectSalesTeam() {
    const trigger = triggerRef.current

    if (!trigger || variant !== 'mobile') return

    beginExit()
    setOpen(false)
    dashboardContextRef.current = null
    setDashboardContext(null)

    const reduceMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false

    actionTimerRef.current =
      window.setTimeout(() => {
        actionTimerRef.current = null
        navigate('/sales-team')
      }, reduceMotion ? 0 : MENU_EXIT_MS)
  }

  function selectProspecting() {
    const trigger = triggerRef.current

    if (!trigger || variant !== 'mobile') return

    beginExit()
    setOpen(false)
    dashboardContextRef.current = null
    setDashboardContext(null)

    const reduceMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false

    actionTimerRef.current =
      window.setTimeout(() => {
        actionTimerRef.current = null
        navigate('/prospecting?list=hot')
      }, reduceMotion ? 0 : MENU_EXIT_MS)
  }

  const menuIsRendered =
    menuState !== 'hidden'

  const desktopCardStyle = {
    '--desktop-origin-x': desktopOrigin.x,
    '--desktop-origin-y': desktopOrigin.y,
  } as DesktopOriginStyle

  const menu = menuIsRendered ? (
    <div
      aria-hidden={
        !open ? 'true' : undefined
      }
      aria-label="Quick actions"
      className={`quick-action-popover is-${menuState}`}
      id={menuId}
      onKeyDown={handleMenuKeyDown}
      role="menu"
      style={
        variant === 'desktop'
          ? desktopCardStyle
          : undefined
      }
    >
      <div className="quick-action-title">
        Quick actions
      </div>

      <button
        className="quick-action-item is-active"
        onClick={selectAddProspect}
        ref={addActionRef}
        role="menuitem"
        type="button"
      >
        <Building2
          aria-hidden="true"
          size={19}
        />

        <span>
          <strong>Add New Prospect</strong>
          <small>
            Create a company and owner record
          </small>
        </span>
      </button>

      {onAddSalesperson ? (
        <button
          className="quick-action-item is-active"
          onClick={selectAddSalesperson}
          ref={salespersonActionRef}
          role="menuitem"
          type="button"
        >
          <UserPlus
            aria-hidden="true"
            size={19}
          />

          <span>
            <strong>Add Salesperson</strong>
            <small>
              Add a member of the sales team
            </small>
          </span>
        </button>
      ) : null}

      <button
        className="quick-action-item is-active"
        onClick={selectReminder}
        ref={reminderActionRef}
        role="menuitem"
        type="button"
      >
        <Bell
          aria-hidden="true"
          size={19}
        />

        <span>
          <strong>Set Reminder</strong>
          <small>
            Schedule a prospect follow-up
          </small>
        </span>
      </button>

      <button
        className={`quick-action-item is-active${
          dashboardContext
            ? ' is-contextual'
            : ''
        }`}
        onClick={selectReview}
        ref={reviewActionRef}
        role="menuitem"
        type="button"
      >
        <ClipboardCheck
          aria-hidden="true"
          size={19}
        />

        <span>
          <strong>
            Review Missing Information
          </strong>

          <small>
            {dashboardContext
              ? `Review ${dashboardContext.label.toLowerCase()}`
              : 'Complete an existing prospect'}
          </small>
        </span>
      </button>
      <button
        className="quick-action-item is-active"
        onClick={selectScript}
        ref={scriptActionRef}
        role="menuitem"
        type="button"
      >
        <FileText
          aria-hidden="true"
          size={19}
        />

        <span>
          <strong>SCRIPT</strong>
          <small>
            Use an approved DM script
          </small>
        </span>
      </button>
      {variant === 'mobile' ? (
        <button
          className="quick-action-item quick-action-prospecting is-active"
          onClick={selectProspecting}
          ref={prospectingActionRef}
          role="menuitem"
          type="button"
        >
          <Target
            aria-hidden="true"
            size={19}
          />

          <span>
            <strong>Prospecting</strong>
            <small>
              Hot · Audit · Leads · Old · Platinum
            </small>
          </span>
        </button>
      ) : null}
      {variant === 'mobile' ? (
        <button
          className="quick-action-item quick-action-prospecting is-active"
          onClick={selectSalesTeam}
          ref={salesTeamActionRef}
          role="menuitem"
          type="button"
        >
          <Users
            aria-hidden="true"
            size={19}
          />

          <span>
            <strong>Sales Team</strong>
            <small>
              Lead ownership and closed business
            </small>
          </span>
        </button>
      ) : null}
    </div>
  ) : null

  return (
    <div
      className={`quick-action quick-action--${variant}`}
      ref={rootRef}
    >
      <button
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={
          variant === 'desktop'
            ? 'Quick actions'
            : open
              ? 'Close quick actions'
              : 'Open quick actions'
        }
        className={`quick-action-trigger${
          open ? ' is-open' : ''
        }`}
        onClick={toggleMenu}
        ref={triggerRef}
        type="button"
      >
        <Plus
          aria-hidden="true"
          size={
            variant === 'mobile'
              ? 32
              : 28
          }
          strokeWidth={
            variant === 'mobile'
              ? 2.25
              : 2
          }
        />
      </button>

      {backdropState !== 'hidden' ? (
        <div
          aria-hidden="true"
          className={`quick-action-backdrop is-${backdropState}`}
          onPointerDown={
            variant === 'desktop'
              ? () => closeMenu(true)
              : undefined
          }
        />
      ) : null}

      {variant === 'desktop' && menu ? (
        <div
          className={`quick-action-desktop-overlay is-${menuState}`}
        >
          {menu}
        </div>
      ) : (
        menu
      )}
    </div>
  )
}
